import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Select } from "@sylviq/ui-react-web/components/ui/select";
import { cn } from "@sylviq/ui-react-web/lib/cn";
import { RefreshCw, SlidersHorizontal } from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DashboardBuilder } from "./DashboardBuilder.js";
import {
  activePreset,
  type AnyDataSource,
  createLocalStorageStore,
  type DashboardState,
  type DashboardStore,
  type Preset,
  type Span,
  normalizeState,
  stateFromDefaults,
  type WidgetConfig,
} from "./model.js";
import {
  builtInWidgets,
  type DashboardLink,
  resolveOptions,
  type WidgetDefinition,
} from "./widgets.js";

const SPAN_CLASS: Record<Span, string> = {
  3: "md:col-span-3",
  4: "md:col-span-4",
  6: "md:col-span-6",
  8: "md:col-span-8",
  12: "md:col-span-12",
};

const AnchorLink: DashboardLink = ({ href, className, children }) => (
  <a href={href} className={className}>
    {children}
  </a>
);

type Result =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; data: unknown };

export interface DashboardProps {
  /** Everything a widget can be bound to. Order is the builder's order. */
  sources: AnyDataSource[];
  /** Built-in layouts, used first and again on "reset". */
  defaults: Preset[];
  /** Where saved layouts live. Defaults to `localStorage` under this key. */
  storageKey?: string;
  store?: DashboardStore;
  /** Provided only for workspace administrators by a server-backed store. */
  onSaveWorkspaceDefault?: (state: DashboardState) => Promise<void>;
  /** Extra widget types, alongside the built-in number, chart, table and
   * checklist. This is the extension seam. */
  widgetTypes?: WidgetDefinition[];
  /** Renders in-app links (router-aware). Defaults to a plain anchor. */
  Link?: DashboardLink;
  /** Lays out the page header around the layout switcher and buttons. */
  renderHeader?: (controls: ReactNode) => ReactNode;
  className?: string;
}

/**
 * A customizable overview: a grid of widgets, each bound to a data source,
 * with named layouts the user can switch between and an in-place builder.
 * Layout is JSON; sources and widget types are registered in code.
 */
export function Dashboard({
  sources,
  defaults,
  storageKey = "sylviq.dashboard.v1",
  store: providedStore,
  onSaveWorkspaceDefault,
  widgetTypes,
  Link = AnchorLink,
  renderHeader,
  className,
}: DashboardProps) {
  const store = useMemo(
    () => providedStore ?? createLocalStorageStore(storageKey),
    [providedStore, storageKey],
  );
  const registry = useMemo(() => {
    const map = new Map<string, WidgetDefinition>();
    for (const definition of [...builtInWidgets, ...(widgetTypes ?? [])]) {
      map.set(definition.type, definition);
    }
    return map;
  }, [widgetTypes]);
  const sourceById = useMemo(
    () => new Map(sources.map((source) => [source.id, source])),
    [sources],
  );

  const [state, setState] = useState<DashboardState>(() =>
    stateFromDefaults(defaults),
  );
  const [layoutLoading, setLayoutLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [results, setResults] = useState<Record<string, Result>>({});
  const [revision, setRevision] = useState(0);

  const commit = useCallback(
    (next: DashboardState) => {
      setState(next);
      void store.write(next).catch(() => {
        // A server failure must not make a user's customization disappear.
        void createLocalStorageStore(storageKey).write(next);
      });
    },
    [store],
  );

  useEffect(() => {
    let cancelled = false;
    setLayoutLoading(true);
    void store
      .read()
      .catch(() => createLocalStorageStore(storageKey).read())
      .then((saved) => {
        if (!cancelled) setState(normalizeState(saved, defaults));
      })
      .finally(() => {
        if (!cancelled) setLayoutLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [defaults, storageKey, store]);

  const preset = activePreset(state);
  // Keyed by the ids themselves so a change that only moves or resizes a
  // widget does not reload every source.
  const wantedKey = [...new Set(preset.widgets.map((w) => w.source))].join("\0");
  const wantedIds = useMemo(
    () => (wantedKey ? wantedKey.split("\0") : []),
    [wantedKey],
  );

  // Load each bound source once per revision. Widgets sharing a source share
  // its result, and a failure in one source never blocks the others.
  useEffect(() => {
    let cancelled = false;
    for (const id of wantedIds) {
      const source = sourceById.get(id);
      if (!source) continue;
      setResults((current) => ({ ...current, [id]: { status: "loading" } }));
      source.load().then(
        (data) => {
          if (!cancelled)
            setResults((current) => ({
              ...current,
              [id]: { status: "ready", data },
            }));
        },
        (cause: unknown) => {
          if (!cancelled)
            setResults((current) => ({
              ...current,
              [id]: {
                status: "error",
                message: cause instanceof Error ? cause.message : String(cause),
              },
            }));
        },
      );
    }
    return () => {
      cancelled = true;
    };
    // `revision` is the refresh trigger.
    // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  }, [wantedIds, sourceById, revision]);

  const refresh = useCallback(() => setRevision((value) => value + 1), []);

  const controls = (
    <div className="flex items-center gap-2">
      {state.presets.length > 1 ? (
        <Select
          aria-label="Overview layout"
          className="w-48"
          value={state.activeId}
          options={state.presets.map((p) => ({ value: p.id, label: p.name }))}
          onChange={(activeId) => {
            if (activeId) commit({ ...state, activeId });
          }}
        />
      ) : null}
      <Button type="button" variant="ghost" size="icon" aria-label="Refresh" title="Refresh" onClick={refresh}>
        <RefreshCw className="size-4" />
      </Button>
      <Button type="button" variant="outline" onClick={() => setBuilding(true)}>
        <SlidersHorizontal className="size-4" /> Customize
      </Button>
    </div>
  );

  return (
    <div className={cn("grid gap-4", className)}>
      {renderHeader ? renderHeader(controls) : <div className="flex justify-end gap-2">{controls}</div>}

      {layoutLoading ? (
        <Placeholder>Loading your overview layout…</Placeholder>
      ) : preset.widgets.length === 0 ? (
        <EmptyState
          title="This layout is empty"
          reason="Add numbers, charts and tables from your data, and arrange them the way you work."
          action={
            <Button type="button" onClick={() => setBuilding(true)}>
              Customize
            </Button>
          }
        />
      ) : (
        <div data-testid="dashboard" className="grid grid-cols-12 gap-4">
          {preset.widgets.map((widget) => (
            <WidgetSlot
              key={widget.id}
              widget={widget}
              definition={registry.get(widget.type)}
              source={sourceById.get(widget.source)}
              result={results[widget.source]}
              Link={Link}
              onRetry={refresh}
            />
          ))}
        </div>
      )}

      <DashboardBuilder
        open={building}
        onOpenChange={setBuilding}
        state={state}
        defaults={defaults}
        sources={sources}
        widgetTypes={[...registry.values()]}
        onSave={(next) => {
          commit(next);
          setBuilding(false);
        }}
        onSaveWorkspaceDefault={
          onSaveWorkspaceDefault
            ? (next) => {
                void onSaveWorkspaceDefault(next);
                setBuilding(false);
              }
            : undefined
        }
      />
    </div>
  );
}

function Placeholder({ children, tone }: { children: ReactNode; tone?: "danger" }) {
  return (
    <div
      className={cn(
        "surface rounded-2xl p-5 text-sm",
        tone === "danger" ? "text-[var(--danger)]" : "text-[var(--muted-foreground)]",
      )}
    >
      {children}
    </div>
  );
}

function WidgetSlot({
  widget,
  definition,
  source,
  result,
  Link,
  onRetry,
}: {
  widget: WidgetConfig;
  definition: WidgetDefinition | undefined;
  source: AnyDataSource | undefined;
  result: Result | undefined;
  Link: DashboardLink;
  onRetry: () => void;
}) {
  let body: ReactNode;
  if (!definition) {
    body = <Placeholder tone="danger">Unknown widget type "{widget.type}".</Placeholder>;
  } else if (!source) {
    body = (
      <Placeholder tone="danger">
        The data source "{widget.source}" is not available. Change it in Customize.
      </Placeholder>
    );
  } else if (source.kind !== definition.kind) {
    body = (
      <Placeholder tone="danger">
        "{source.label}" cannot be shown as a {definition.label.toLowerCase()}.
      </Placeholder>
    );
  } else if (!result || result.status === "loading") {
    body = <Placeholder>Loading {source.label.toLowerCase()}…</Placeholder>;
  } else if (result.status === "error") {
    body = (
      <Placeholder tone="danger">
        <p role="alert">{result.message}</p>
        <Button type="button" variant="secondary" size="sm" className="mt-2" onClick={onRetry}>
          Retry
        </Button>
      </Placeholder>
    );
  } else {
    body = definition.render({
      data: result.data as never,
      title: widget.title?.trim() || source.label,
      options: resolveOptions(definition, widget),
      Link,
    });
  }
  // A widget that renders nothing (a finished checklist) drops out of the
  // grid entirely, so the cells around it close up.
  if (body === null) return null;
  return <div className={cn("col-span-12 min-w-0", SPAN_CLASS[widget.span])}>{body}</div>;
}
